import ApiService from "./api.service";
import UtilService from "@/gapp-components/services/util.service";

const OcrService = {
  store: null,
  ocrAttempts: 0,
  ocrMaxAttempts: 20,
  ocrScanCancel: false,

  init(opts) {
    if (opts.store) {
      this.store = opts.store;
    }
  },

  cancelOcrScan() {
    this.ocrScanCancel = true;
  },

  isOcrScanCancelled() {
    return this.ocrScanCancel;
  },

  processInvoice(upload, serialNumberPrefixes = [], serialNumberSuffixes = []) {
    let s3Path = upload !== null ? upload.awsS3Url : null;
    let httpUrl = upload !== null ? upload.href : null;
    let isPublic = this.store && this.store.getters.isPublic;
    let selectedProgram = this.store ? this.store.getters.selectedProgram : null;

    if (!s3Path && !httpUrl) {
      return Promise.reject(new Error("No valid URL found for upload."));
    }

    if (isPublic && (!selectedProgram || !selectedProgram.programGroup || !selectedProgram.programGroup.programGroupKey)) {
      return Promise.reject(new Error("No valid program group found for OCR."));
    }

    let queryParams = new URLSearchParams();
    if (isPublic) {
      queryParams.append("programGroupKey", selectedProgram.programGroup.programGroupKey);
    }
    if (serialNumberPrefixes.length > 0) {
      queryParams.append("serialNumberPrefixes", serialNumberPrefixes.join(","));
    }
    if (serialNumberSuffixes.length > 0) {
      queryParams.append("serialNumberSuffixes", serialNumberSuffixes.join(","));
    }

    if (s3Path) {
      queryParams.append("s3Path", s3Path);
    } else {
      queryParams.append("httpUrl", httpUrl);
    }
    let url = isPublic ? "/ocr/invoice/public?" : "/ocr/invoice?";
    return ApiService.post(url + queryParams.toString());
  },

  statusInvoice(jobId) {
    let url = "/ocr/status/" + jobId;
    if (this.store && this.store.getters.isPublic) {
      url += "/public";
    }
    return ApiService.get(url);
  },

  resultsInvoice(jobId) {
    let url = "/ocr/results/" + jobId;
    if (this.store && this.store.getters.isPublic) {
      url += "/public";
    }
    return ApiService.get(url);
  },

  async processInvoiceAndGetResult(upload, serialNumberPrefixes = [], serialNumberSuffixes = []) {
    this.ocrScanCancel = false;
    this.ocrAttempts = 0;
    let response = await this.processInvoice(upload, serialNumberPrefixes, serialNumberSuffixes);
    return this.checkProcessedInvoice(response.data);
  },

  async checkProcessedInvoice(ocrResult) {
    if (!ocrResult) {
      return Promise.reject("OCR Error");
    }
    if (ocrResult.jobId) {
      return this.checkProcessedInvoiceJob(ocrResult.jobId);
    }
    if (ocrResult.success) {
      return ocrResult;
    }
    return Promise.reject(ocrResult.message ? ocrResult.message : "OCR Error: " + JSON.stringify(ocrResult));
  },

  async checkProcessedInvoiceJob(jobId) {
    if (this.ocrAttempts === this.ocrMaxAttempts) {
      return Promise.reject("Reached max attempts.");
    }
    this.ocrAttempts++;

    await UtilService.sleep(5000);

    if (this.ocrScanCancel) {
      return Promise.reject("OCR cancelled");
    }

    let response;
    try {
      response = await this.statusInvoice(jobId);
    } catch (error) {
      if (error && error.response && error.response.status === 404) {
        return this.checkProcessedInvoiceJob(jobId);
      }
      return Promise.reject(error);
    }
    let job = response.data;
    if (job.status === "COMPLETED") {
      if (job.result) {
        return job.result;
      }
      let resultResponse = await this.resultsInvoice(jobId);
      return resultResponse.data;
    }
    if (job.status === "FAILED") {
      return Promise.reject(job.errorMessage ? job.errorMessage : "OCR Error");
    }
    return this.checkProcessedInvoiceJob(jobId);
  },

  async startInvoiceScan(upload, isPublic, promotionId = null) {
    const path = isPublic
      ? `/api/ocr/invoiceScan/${promotionId}/${upload.id}/public?publicToken=${upload.publicToken}`
      : `/api/ocr/invoice/${upload.id}/scan`;

    const { data } = isPublic ? await ApiService.get(path) : await ApiService.post(path);
    return this.getInvoiceScanResult(data, upload.id, 1, isPublic, upload.publicToken);
  },

  async getInvoiceScanResult(ocrResponse, uploadId, attempt = 1, isPublic, publicToken) {
    let evalResult = this.evaluateOcrResponse(ocrResponse);
    if (evalResult.done) return evalResult.result;

    if (attempt > this.ocrMaxAttempts) {
      throw new Error("Reached max attempts.");
    }

    await UtilService.sleep(2000);

    const resultPath = isPublic
      ? `/api/ocr/invoice/${uploadId}/result/public?publicToken=${publicToken}`
      : `/api/ocr/invoice/${uploadId}/result`;

    const { data } = await ApiService.get(resultPath);
    evalResult = this.evaluateOcrResponse(data);
    if (evalResult.done) return evalResult.result;

    return this.getInvoiceScanResult(data, uploadId, attempt + 1, isPublic);
  },

  evaluateOcrResponse(ocrResponse) {
    if (ocrResponse?.status === "READY") {
      return { done: true, result: ocrResponse };
    }
    if (ocrResponse?.status === "FAILED") {
      const msg = ocrResponse?.statusMessage || "OCR failed.";
      throw new Error(msg);
    }
    return { done: false };
  },

  async invoiceScan(promotionId, uploadId, isPublic, publicToken) {
    this.ocrScanCancel = false;
    let url = "/api/ocr/invoiceScan/" + promotionId + "/" + uploadId;
    if (isPublic) {
      url += "/public?publicToken=" + publicToken;
    }
    this.ocrAttempts = 0;
    return await ApiService.get(url).then(({ data }) => {
      return this.checkInvoiceScan(data, promotionId, uploadId, isPublic, publicToken);
    });
  },

  async checkInvoiceScan(ocrResult, promotionId, uploadId, isPublic, publicToken) {
    let url = "/api/ocr/checkInvoiceScan/" + promotionId + "/" + uploadId;
    if (isPublic) {
      url += "/public?publicToken=" + publicToken;
    }
    if (ocrResult.status === "READY" && this.ocrAttempts < this.ocrMaxAttempts) {
      return ocrResult;
    } else if (ocrResult.status === "PENDING" && this.ocrAttempts < this.ocrMaxAttempts) {
      this.ocrAttempts++;

      await new Promise(resolve =>
        setTimeout(() => {
          resolve();
        }, 5000)
      );

      if (!this.ocrScanCancel) {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error("Operation timed out"));
          }, 5000); // 5 seconds timeout
        });

        return Promise.race([ApiService.get(url), timeoutPromise])
          .then(({ data }) => {
            return this.checkInvoiceScan(data, promotionId, uploadId, isPublic, publicToken);
          })
          .catch(error => {
            if (error.message === "Operation timed out") {
              return this.checkInvoiceScan(ocrResult, promotionId, uploadId, isPublic, publicToken);
            }
            return Promise.reject(error);
          });
      }
    } else if (this.ocrAttempts === this.ocrMaxAttempts) {
      return Promise.reject("Reached max attempts.");
    } else {
      return Promise.reject("OCR Error: " + JSON.stringify(ocrResult));
    }
  }
};

export default OcrService;
